/**
 * UIFilterController - Dynamic UI Updates Based on AI Commands
 * TASK-206 Phase 3: AI Chat Assistant
 *
 * Executes UI update commands from the AI query engine to dynamically
 * filter, highlight, and navigate the interface based on natural language queries
 */

import { UICommand } from './AIQueryEngine';

export interface UIState {
  activeTab: string;
  filteredISIN: string | null;
  selectedPortfolios: string[];
  highlightedSection: string | null;
  scrollTarget: string | null;
}

export class UIFilterController {
  private onTabChange: (tab: string) => void;
  private onFilterChange: (filter: any) => void;
  private onHighlight: (section: string) => void;
  private onScroll: (target: string) => void;

  constructor(callbacks: {
    onTabChange: (tab: string) => void;
    onFilterChange: (filter: any) => void;
    onHighlight: (section: string) => void;
    onScroll: (target: string) => void;
  }) {
    this.onTabChange = callbacks.onTabChange;
    this.onFilterChange = callbacks.onFilterChange;
    this.onHighlight = callbacks.onHighlight;
    this.onScroll = callbacks.onScroll;
  }

  /**
   * Execute a batch of UI commands
   */
  async executeCommands(commands: UICommand[]): Promise<void> {
    for (const command of commands) {
      await this.executeCommand(command);
      // Small delay between commands for smooth UX
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  /**
   * Execute a single UI command
   */
  private async executeCommand(command: UICommand): Promise<void> {
    switch (command.type) {
      case 'SWITCH_TAB':
        this.onTabChange(command.payload);
        break;

      case 'FILTER_POSITIONS':
        this.onFilterChange(command.payload);
        break;

      case 'HIGHLIGHT_SECTION':
        this.onHighlight(command.payload);
        // Auto-remove highlight after 5 seconds
        setTimeout(() => {
          this.onHighlight('');
        }, 5000);
        break;

      case 'SELECT_PORTFOLIOS':
        this.onFilterChange({ comparePortfolios: command.payload });
        break;

      case 'SCROLL_TO':
        this.onScroll(command.payload);
        break;

      default:
        console.warn('Unknown UI command type:', command.type);
    }
  }

  /**
   * Clear all filters and highlights
   */
  clearAll(): void {
    this.onFilterChange({ comparePortfolios: [], filteredISIN: null });
    this.onHighlight('');
  }
}

export default UIFilterController;
